const LEAD_CAPTURE_DRAFT_KEY = 'lead-capture-form-draft';

class LeadCaptureModal {
  constructor(modal) {
    this.modalId = modal.id;
    this.isDesignMode = Boolean(window.Shopify && window.Shopify.designMode);
    this.lastTrigger = null;
    this.isSelectedInEditor = false;
    this.submissionPrepared = false;

    this.captureRefs(modal);

    this.boundHandleClick = this.handleClick.bind(this);
    this.boundHandleKeydown = this.handleKeydown.bind(this);

    document.addEventListener('click', this.boundHandleClick);
    document.addEventListener('keydown', this.boundHandleKeydown);

    if (this.isDesignMode) {
      this.bindEditorPreviewEvents();
    }

    this.checkAutoOpen();
  }

  // (Re)reads every DOM reference this instance depends on and rewires the
  // listeners scoped to those elements. Called on construction, and again
  // whenever the theme editor swaps in freshly re-rendered section HTML
  // (see the shopify:section:load handler below) — a settings/block change
  // replaces the whole section subtree, which would otherwise leave this
  // instance holding references to now-detached nodes.
  captureRefs(modal) {
    this.modal = modal;
    this.dialog = modal.querySelector('.lead-modal__dialog');
    this.sectionId = modal.dataset.sectionId || '';

    this.formState = modal.querySelector('[data-lead-modal-form-state]');
    this.successState = modal.querySelector('[data-lead-modal-success-state]');

    this.form = modal.querySelector('form');
    this.formId = this.form ? this.form.id : null;
    this.submitButton = modal.querySelector('[data-submit-btn]');

    this.productsWrapper = modal.querySelector('.lead-modal__products');
    this.productsRequired = this.productsWrapper ? this.productsWrapper.dataset.productsRequired === 'true' : false;
    this.productError = modal.querySelector('[data-products-error]');
    this.productCheckboxes = modal.querySelectorAll('[data-product-checkbox]');
    this.productsField = modal.querySelector('[data-products-field]');

    // Field roles are assigned per-block in the theme editor (see sections/lead-capture-modal.liquid),
    // so these lookups keep working no matter how a merchant adds, removes, or reorders blocks.
    this.nameField = modal.querySelector('[data-field-role="name"]');
    this.emailField = modal.querySelector('[data-field-role="email"]');
    this.phoneField = modal.querySelector('[data-field-role="phone"]');
    this.cityProvinceField = modal.querySelector('[data-field-role="city_province"]');
    this.commentField = modal.querySelector('[data-field-role="notes"]');

    // Bound to both the button's click and the form's submit: Shopify layers invisible
    // hCaptcha verification on top of this form, which intercepts the first 'submit' event
    // and later completes the real submission via form.submit() — a call that does not fire
    // a 'submit' event at all. Click always fires first and can't be skipped that way, so the
    // pre-submission work (Klaviyo, message composition, draft save, redirect tagging) runs
    // there; the submit listener is a fallback for implicit (Enter-key) submissions, guarded
    // so it never runs twice for the same submission.
    if (this.submitButton) {
      this.submitButton.addEventListener('click', this.prepareSubmission.bind(this));
    }
    if (this.form) {
      this.form.addEventListener('submit', this.prepareSubmission.bind(this));
    }

    this.productCheckboxes.forEach((checkbox) => {
      checkbox.addEventListener('change', () => {
        if (this.hasProductSelected()) this.hideProductError();
      });
    });

    this.bindSubmitState();
    this.bindFieldValidation();
  }

  // Format-checks the email/phone fields ourselves rather than trusting the browser's
  // native constraint validation for them: type="email" validation varies in strictness
  // across browsers, and pattern-attribute enforcement on type="tel" inputs is
  // inconsistent (notably unreliable in Safari), so relying on field.checkValidity()
  // alone let malformed values through silently in some browsers. An empty value isn't
  // flagged here — that's the "required" field's job, checked separately.
  isFieldFormatValid(field) {
    if (!field) return true;
    const value = field.value.trim();
    if (value === '') return true;

    if (field === this.emailField) {
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
    }
    if (field === this.phoneField) {
      const digits = value.replace(/\D/g, '');
      return digits.length === 10 || (digits.length === 11 && digits.charAt(0) === '1');
    }
    return true;
  }

  // Enables the submit button only once every required field the merchant has
  // configured (via blocks) passes native validation, and — if the product
  // picker block requires it — at least one product is selected. Generic over
  // however many/whichever fields exist, so it keeps working no matter how a
  // merchant edits the block list.
  bindSubmitState() {
    if (!this.submitButton || !this.form) return;

    const validLabel = this.submitButton.dataset.validLabel || 'Submit';
    const invalidLabel = this.submitButton.dataset.invalidLabel || 'Please fill out required fields';

    const update = () => {
      const fieldsValid = this.form.checkValidity()
        && this.isFieldFormatValid(this.emailField)
        && this.isFieldFormatValid(this.phoneField);
      const productsValid = this.hasProductSelected();

      if (fieldsValid && productsValid) {
        this.submitButton.classList.remove('button--invalid');
        this.submitButton.disabled = false;
        this.submitButton.textContent = validLabel;
      } else {
        this.submitButton.classList.add('button--invalid');
        this.submitButton.disabled = true;
        this.submitButton.textContent = invalidLabel;
      }
    };

    this.form.addEventListener('input', update);
    this.form.addEventListener('change', update);
    update();
  }

  // Shows a red outline + inline "Invalid entry" message on the email/phone fields once
  // the shopper has left the field (blur) with a value that fails our own format check —
  // never before they've had a chance to type anything, and never for a merely-empty
  // required field (that's handled by bindSubmitState/native validation on submit instead).
  bindFieldValidation() {
    [this.emailField, this.phoneField].forEach((field) => {
      if (!field) return;
      const wrapper = field.closest('[data-field-wrapper]');
      const errorEl = wrapper ? wrapper.querySelector('[data-field-error]') : null;
      if (!wrapper || !errorEl) return;

      const evaluate = () => {
        const touched = wrapper.dataset.touched === 'true';
        const invalid = touched && field.value.trim() !== '' && !this.isFieldFormatValid(field);
        wrapper.classList.toggle('lead-modal__field--invalid', invalid);
        errorEl.hidden = !invalid;
      };

      field.addEventListener('blur', () => {
        wrapper.dataset.touched = 'true';
        evaluate();
      });
      field.addEventListener('input', evaluate);
    });
  }

  // Lets a merchant preview the modal just by clicking its section in the theme
  // editor sidebar, instead of having to click a trigger on the live page —
  // same convention as sections/contact-success-modal.liquid.
  bindEditorPreviewEvents() {
    document.addEventListener('shopify:section:select', (event) => {
      if (event.detail && event.detail.sectionId === this.sectionId) {
        this.isSelectedInEditor = true;
        this.showFormState();
        this.open();
      }
    });

    document.addEventListener('shopify:section:deselect', (event) => {
      if (event.detail && event.detail.sectionId === this.sectionId) {
        this.isSelectedInEditor = false;
        this.close();
      }
    });

    // Every settings/block edit makes the theme editor re-render this section's
    // HTML from scratch and swap it into the page, which silently closes the
    // modal (the fresh markup starts `hidden` again) and leaves this instance
    // pointing at now-detached nodes. Re-point it at the new DOM and, if the
    // section is still selected for editing, reopen it so the preview doesn't
    // blink shut on every keystroke.
    document.addEventListener('shopify:section:load', (event) => {
      if (!event.detail || event.detail.sectionId !== this.sectionId) return;

      const freshModal = document.getElementById(this.modalId);
      if (!freshModal) return;

      this.captureRefs(freshModal);
      window.leadCaptureModals[freshModal.id] = this;

      if (this.isSelectedInEditor) {
        this.showFormState();
        this.open();
      }
    });
  }

  getSelectedProductTitles() {
    return Array.from(this.productCheckboxes)
      .filter((checkbox) => checkbox.checked)
      .map((checkbox) => {
        const title = checkbox.closest('.lead-modal__product-card').querySelector('.lead-modal__product-title');
        return title ? title.textContent.trim() : '';
      })
      .filter(Boolean);
  }

  hasProductSelected() {
    if (!this.productsRequired) return true;
    return Array.from(this.productCheckboxes).some((checkbox) => checkbox.checked);
  }

  // Checks the product-picker card whose value (the product_item block's display
  // title — see sections/lead-capture-modal.liquid) exactly matches `title`, clearing
  // any other selection. Driven by a trigger's data-preselect-product attribute (see
  // snippets/lead-capture-modal-trigger.liquid), e.g. a product page's "Get a Quote"
  // button pre-selecting that page's product.
  preselectProduct(title) {
    if (!this.productCheckboxes.length) return;
    const normalized = title.trim().toLowerCase();
    let matched = false;

    this.productCheckboxes.forEach((checkbox) => {
      const shouldCheck = checkbox.value.trim().toLowerCase() === normalized;
      checkbox.checked = shouldCheck;
      if (shouldCheck) matched = true;
    });

    if (!matched) return;
    this.hideProductError();
    if (this.form) this.form.dispatchEvent(new Event('change', { bubbles: true }));
  }

  showProductError() {
    if (!this.productError) return;
    this.productError.hidden = false;
    this.productError.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }

  hideProductError() {
    if (!this.productError) return;
    this.productError.hidden = true;
  }

  composeMessage(productTitles) {
    if (!this.productsField) return;
    this.productsField.value = productTitles.join(', ');
  }

  trackKlaviyo(productTitles) {
    if (typeof window.klaviyo === 'undefined' || typeof window.klaviyo.identify !== 'function') return;

    const fullName = this.nameField ? this.nameField.value.trim() : '';
    const [firstName, ...rest] = fullName.split(/\s+/).filter(Boolean);
    const lastName = rest.join(' ');

    // The city/province field is a single "City, Province" combined dropdown
    // (see sections/lead-capture-modal.liquid's city_province_field block),
    // so split it back into two properties for Klaviyo.
    let city = '';
    let province = '';
    if (this.cityProvinceField && this.cityProvinceField.value) {
      const [cityPart, provincePart] = this.cityProvinceField.value.split(',');
      city = (cityPart || '').trim();
      province = (provincePart || '').trim();
    }

    window.klaviyo.identify({
      $email: this.emailField ? this.emailField.value.trim() : '',
      $first_name: firstName || '',
      $last_name: lastName || '',
      $phone_number: this.phoneField ? this.phoneField.value.trim() : '',
      City: city,
      Province: province,
    });

    window.klaviyo.track('Submitted Product Interest Form', {
      'Products of Interest': productTitles,
    });
  }

  // Generic over whatever fields the merchant has configured as blocks (including custom
  // ones), rather than a fixed list — so nothing typed is lost on error, no matter which
  // blocks are present.
  saveDraft() {
    if (!this.form) return;

    const draft = {};
    Array.from(this.form.elements).forEach((field) => {
      if (!field.name) return;
      if (field.type === 'checkbox') {
        if (!draft[field.name]) draft[field.name] = [];
        if (field.checked) draft[field.name].push(field.value);
      } else {
        draft[field.name] = field.value;
      }
    });

    try {
      window.sessionStorage.setItem(LEAD_CAPTURE_DRAFT_KEY, JSON.stringify(draft));
    } catch (error) {
      // sessionStorage unavailable — a server error just won't be able to restore what they typed.
    }
  }

  restoreDraft() {
    if (!this.form) return;

    let raw = null;
    try {
      raw = window.sessionStorage.getItem(LEAD_CAPTURE_DRAFT_KEY);
    } catch (error) {
      raw = null;
    }
    if (!raw) return;

    try {
      const draft = JSON.parse(raw);
      Array.from(this.form.elements).forEach((field) => {
        if (!field.name || !(field.name in draft)) return;
        if (field.type === 'checkbox') {
          field.checked = Array.isArray(draft[field.name]) && draft[field.name].includes(field.value);
        } else {
          field.value = draft[field.name];
        }
      });
    } catch (error) {
      // malformed draft — nothing to restore.
    }

    this.clearDraft();
  }

  clearDraft() {
    try {
      window.sessionStorage.removeItem(LEAD_CAPTURE_DRAFT_KEY);
    } catch (error) {
      // no-op
    }
  }

  showSuccessState() {
    this.modal.classList.add('lead-modal--success');
  }

  showFormState() {
    this.modal.classList.remove('lead-modal--success');
  }

  prepareSubmission(event) {
    if (!this.hasProductSelected()) {
      event.preventDefault();
      this.showProductError();
      return;
    }

    if (this.submissionPrepared) return;
    this.submissionPrepared = true;

    const productTitles = this.getSelectedProductTitles();
    this.composeMessage(productTitles);
    this.trackKlaviyo(productTitles);
    this.saveDraft();

    if (this.formId) {
      try {
        window.sessionStorage.setItem('shopify-form-submitted', this.formId);
      } catch (error) {
        // sessionStorage unavailable (e.g. private browsing) — auto-open/restore just won't fire.
      }
    }
  }

  checkAutoOpen() {
    if (!this.formId) return;

    let submittedFormId = null;
    try {
      submittedFormId = window.sessionStorage.getItem('shopify-form-submitted');
    } catch (error) {
      submittedFormId = null;
    }

    if (!submittedFormId || submittedFormId !== this.formId) return;

    try {
      window.sessionStorage.removeItem('shopify-form-submitted');
    } catch (error) {
      // no-op
    }

    const marker = document.querySelector('meta[name="shopify-contact-status"]');
    const status = marker ? marker.getAttribute('content') : null;

    if (status === 'success') {
      this.clearDraft();
      this.showSuccessState();
      this.open();
    } else if (status === 'error') {
      this.showFormState();
      this.restoreDraft();
      this.open();
    }
  }

  handleClick(event) {
    // Two ways in: data-lead-modal-open for code-rendered triggers (the
    // lead-capture-modal-trigger snippet), and a magic URL fragment so a merchant can point
    // *any* theme-editor "Button link" field (image-with-text, header CTA, multicolumn, rich
    // text, etc.) at the modal without touching code.
    const trigger = event.target.closest(`[data-lead-modal-open="${this.modalId}"]`);
    const linkTrigger = event.target.closest('a[href*="#lead-capture-modal"]');
    if (trigger || linkTrigger) {
      event.preventDefault();
      this.lastTrigger = trigger || linkTrigger;
      const preselectProduct = this.lastTrigger.dataset.preselectProduct;
      if (preselectProduct) this.preselectProduct(preselectProduct);
      this.open();
      return;
    }

    if (!this.isOpen()) return;

    if (event.target.closest('[data-lead-modal-close]') && this.modal.contains(event.target.closest('[data-lead-modal-close]'))) {
      event.preventDefault();
      this.close();
    }
  }

  handleKeydown(event) {
    if (event.code && event.code.toUpperCase() === 'ESCAPE' && this.isOpen()) {
      this.close();
    }
  }

  isOpen() {
    return !this.modal.hasAttribute('hidden');
  }

  open() {
    if (this.isOpen()) return;

    this.modal.removeAttribute('hidden');
    document.body.classList.add('overflow-hidden');

    const activeState = this.modal.classList.contains('lead-modal--success') ? this.successState : this.formState;
    const searchRoot = activeState || this.modal;
    const firstFocusable = searchRoot.querySelector('input, select, textarea, button, a[href]');
    if (typeof trapFocus === 'function') {
      trapFocus(this.dialog, firstFocusable || this.dialog);
    } else if (firstFocusable) {
      firstFocusable.focus();
    }
  }

  close() {
    this.modal.setAttribute('hidden', 'hidden');
    document.body.classList.remove('overflow-hidden');
    this.showFormState();

    if (typeof removeTrapFocus === 'function') {
      removeTrapFocus(this.lastTrigger || null);
    }
    this.lastTrigger = null;
  }
}

if (!window.leadCaptureModals) {
  window.leadCaptureModals = {};
  document.querySelectorAll('[data-lead-capture-modal]').forEach((modal) => {
    window.leadCaptureModals[modal.id] = new LeadCaptureModal(modal);
  });
}
